/**
 * @file index.tsx
 *
 * @fileoverview Shows a public organization profile.
 */

import React, { useContext, useEffect } from 'react';
import { OrganizationBannerWidget } from '~/jobiqo/components/Organization/Widgets/Banner';
import { OrganizationInfoBoxWidget } from '~/jobiqo/components/Organization/Widgets/InfoBox';
import { OrganizationAboutWidget } from '~/jobiqo/components/Organization/Widgets/About';
const OrganizationGalleryWidget = dynamic(
  () => import('~/jobiqo/components/Organization/Widgets/Gallery'),
  {
    ssr: false,
    loading: () => <OrganizationGalleryPlaceholder />,
  },
);
const OrganizationVideoGalleryWidget = dynamic(
  () => import('~/jobiqo/components/Organization/Widgets/VideoGallery'),
  {
    ssr: false,
    loading: () => <OrganizationGalleryPlaceholder />,
  },
);
import { OrganizationLocationsWidget } from '~/jobiqo/components/Organization/Widgets/Locations';
import { FormattedMessage } from 'react-intl';
import { ContentErrorList } from '~/jobiqo/components/ContentErrorList';
import { Layout } from '~/jobiqo/components/Layout';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { JobsByOrganizationProfileId } from '~/jobiqo/components/Organization/JobsByOrganizationProfileId';
import dynamic from 'next/dynamic';
import { OrganizationSubGroups } from '~/jobiqo/components/Organization/OrganizationSubGroups';
import {
  analyticsDispatchDelayed,
  EVENTS,
  PAGE_TYPES,
} from '~/jobiqo/lib/analytics';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { getOrganizationProfileDimensions } from '../utils/organization_analytics';
import { OrganizationDetailSidebar } from '~/jobiqo/components/Organization/Detail/Sidebar';
import OrganizationMiniSite from '~/jobiqo/components/Organization/MiniSite';
import OrganizationFAQWidget from '~/jobiqo/components/Organization/Widgets/FAQ';
import OrganizationBlogPostsWidget from '~/jobiqo/components/Organization/Widgets/BlogPosts';
import { OrganizationGalleryPlaceholder } from '~/vcl/organisms/05-placeholders/06-organization/02-gallery';
import { OrganizationDetailPlaceholder } from '~/vcl/organisms/05-placeholders/06-organization/01-detail';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Box } from '~/vcl/atoms/07-box';
import { EmptyElement } from '~/vcl/molecules/02-empty';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { OfficeIcon } from '~/vcl/icons/Office';

interface OrganizationPublicProfileProps {
  path: string;
  loading: boolean;
  error: boolean;
  data: any;
}

export const OrganizationPublicProfile = ({
  loading,
  error,
  data,
  path,
}: OrganizationPublicProfileProps) => {
  const userContext: UserContextInterface = useContext(UserContext);

  let errorList: React.ReactNode = null;
  let organization: OrganizationProfile | null = null;

  if (loading) {
    // We need a skeleton.
    return <OrganizationDetailPlaceholder />;
  }

  if (error) {
    errorList = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="organization_profile_error"
          defaultMessage="Error getting information from profile."
        />
      </MessageAlert>
    );
  }

  if (data?.organizationProfileByPath?.errors?.length > 0) {
    errorList = (
      <ContentErrorList errors={data?.organizationProfileByPath?.errors} />
    );
  }

  // Returns all jobs data, including facets.

  if (data) {
    organization = data.organizationProfileByPath.content;
  }

  useEffect(() => {
    // Log organization profile visits and mark minisite home visits via page_type.
    if (organization) {
      analyticsDispatchDelayed(
        EVENTS.ORGANIZATION_PROFILE_VISIT,
        getOrganizationProfileDimensions(
          organization,
          userContext.user,
          organization.isMinisiteEnabled
            ? PAGE_TYPES.MINISITE
            : PAGE_TYPES.ORGANIZATION_PROFILE,
        ),
      );
    }
  }, [organization, userContext.user]);

  if (!data) {
    return (
      <Layout fullWidthContainer={true}>
        <Main size="small">
          <Box>
            <EmptyElement icon={<OfficeIcon title="Company" />}>
              <Text tag="h1" fontSize={4}>
                <FormattedMessage
                  id="company_detail_not_found_title"
                  defaultMessage="Could not find company"
                />
              </Text>
              <FormattedMessage
                id="company_detail_not_found"
                defaultMessage={`The company you are trying to access does not exist or could not be found.`}
              />
            </EmptyElement>
          </Box>
        </Main>
      </Layout>
    );
  }

  if (organization && organization.isMinisiteEnabled) {
    return <OrganizationMiniSite organization={organization} path={path} />;
  }

  return (
    <Layout fullWidthContainer={true}>
      {organization ? (
        <div>
          <OrganizationBannerWidget organization={organization} />
          <OrganizationInfoBoxWidget organization={organization} />
          <Main>
            <Spacing size="medium" />
            {errorList && (
              <>
                {errorList}
                <Spacing />
              </>
            )}
            <OrganizationDetailSidebar organization={organization}>
              <OrganizationAboutWidget organization={organization} />
              <Spacing size="large" />
              <OrganizationGalleryWidget organization={organization} />
              <Spacing size="large" />
              <OrganizationVideoGalleryWidget organization={organization} />
              <Spacing size="large" />
              <JobsByOrganizationProfileId organization={organization} />
              <Spacing size="large" />
              <OrganizationSubGroups organization={organization} />
              <Spacing size="large" />
              <OrganizationFAQWidget organization={organization} />
              <Spacing size="large" />
              <OrganizationBlogPostsWidget organization={organization} />
              <Spacing size="large" />
              <OrganizationLocationsWidget organization={organization} />
              <Spacing size="large" />
            </OrganizationDetailSidebar>
          </Main>
        </div>
      ) : (
        <Main size="small">
          <Box>
            <EmptyElement
              icon={<OfficeIcon title="lock" />}
              iconWidth="300"
              iconHeight="180"
            >
              <FormattedMessage
                id="organization_not_found"
                defaultMessage="Could not find the organization in this URL"
              />
            </EmptyElement>
          </Box>
        </Main>
      )}
      <Spacing marginTop={[8, 12]} />
    </Layout>
  );
};
export default OrganizationPublicProfile;
